import {Injectable} from '@angular/core';
import {io} from 'socket.io-client';
import {environment} from '../../environments/environment';
import {BehaviorSubject, Observable, Subject} from 'rxjs';

export type ConnectionState = 'synced' | 'syncing' | 'local';
import {Customer} from '../_models/customer';
import {CustomerRewardsService} from './customerRewards.service';
import {CartService} from './cart.service';
import {User} from '../_models';
import {CloverOrderService} from './cloverOrder.service';
import {AESEncryptDecryptService} from './aesencrypt-decrypt.service';
import {Router} from '@angular/router';
import {AlertService} from './alert.service';
import {AtmoicOrderService} from './atomic-order.service';
import {CloverWebhook} from '../_models/cloverWebhook';
import * as _ from 'lodash';
import {MatDialog} from '@angular/material/dialog';
import {Discount} from '../_models/discount';
import {GeneralWebhook} from '../_models/generalWebhook';
import {BumpedOrderModel} from '../_models/bumpedOrderModel';
import {Constants} from '../constants/constants';
import {SquareApiService} from './square-api.service';
import {Order} from '../_models/order';
import {UtilsService} from './utils.service';

@Injectable({
  providedIn: 'root'
})
export class SocketioService {
  socket;
  currentUser: User;
  selectedRole;
  orderId;
  merchantId;
  rewardsCustomer: Customer;
  rewardsUpdated = false;
  rewardsRedeemed = false;
  bumpedOrders = [];
  orders = [];
  OrdersSubject$ = new BehaviorSubject<Order[]>([]);
  bumpedOrdersSubject$ = new BehaviorSubject<BumpedOrderModel[]>([]);
  squareDeviceId: string;
  deviceAlias: any;
  newOrderCreated = new Subject<boolean>();
  newOrderId = new BehaviorSubject<string | null>(null);
  paymentCompleted$ = new Subject<{orderId: string}>();
  connectionState$ = new BehaviorSubject<ConnectionState>('local');
  private lastEmittedOrderId: string | null = null;
  private cashierRegisterActive = false;
  private socketLifecycleRegistered = false;
  // Clover payment ids already surfaced to the cashier-register completion
  // path this session. Dedups (a) Clover webhook retries and (b) our two
  // socket listeners (`_cashier` + main merchant) both firing for one
  // payment — either would double-increment splitTenderPaidCount and
  // complete a split order early / undercharge. Distinct split portions are
  // distinct Clover payments → distinct ids, so they are NOT suppressed.
  private seenCashierPaymentIds = new Set<string>();


  constructor(private rewardsService: CustomerRewardsService, private cartService: CartService, private router: Router,
              private alertService: AlertService, private cloverOrderService: CloverOrderService, private utilsService: UtilsService,
              private squareApiService: SquareApiService, private atmoicOrderService: AtmoicOrderService, private dialog: MatDialog,
              private _AESEncryptDecryptService: AESEncryptDecryptService) {
    this.currentUser = utilsService.getCurrentUser();
    this.selectedRole = utilsService.getSelectedRoleFromSession();
    this.merchantId = this.currentUser.merchantId;
    if ((this.selectedRole === Constants.KIOSK && this.currentUser.takePayment.devicePaymentEnabled
      && (this.currentUser.takePayment.cloverConnectorPayment || this.currentUser.takePayment.scanToPayPayment)
      && this.currentUser.takePayment.paymentRequired && this.currentUser.merchantId)) {
      this.setupSocketConnection();
    } else if ((this.selectedRole === 'PaymentsDisplay' || this.selectedRole === Constants.ORDERS_DISPLAY
      || (this.selectedRole === Constants.TABLE && this.currentUser.takePayment.devicePaymentEnabled
        && this.currentUser.takePayment.scanToPayPayment))) {
      this.setupSocketConnection();
    } else if (this.currentUser.orgSettings?.accountType && this.selectedRole === Constants.KIOSK) {
      this.setupSocketConnection();
    }

    if (this.currentUser.orgSettings.accountType === Constants.CLOVER) {
      this.loadRewards();
    }

    this.addListenerListenForOrg();
  }

  private ensureSocket() {
    if (!this.socket) {
      this.socket = io(`${environment.apiServerUrl}`);
    }
    if (!this.socketLifecycleRegistered && this.socket) {
      this.socketLifecycleRegistered = true;
      this.socket.on('connect', () => this.connectionState$.next('synced'));
      this.socket.on('disconnect', () => this.connectionState$.next('local'));
      this.socket.on('reconnect_attempt', () => this.connectionState$.next('syncing'));
      this.socket.on('reconnecting', () => this.connectionState$.next('syncing'));
    }
  }

  setupSocketConnection() {
    this.ensureSocket();
    if (this.currentUser.orgSettings.accountType === Constants.CLOVER) {
      this.addListenerListenForMerchant();
    } else if (this.currentUser.orgSettings.accountType === Constants.SQUARE) {
      this.addListenerListenForSquareMerchant();
      if (!environment.production) {
        this.addListenerListenForSquareDevWebhookTestMerchant();
      }
    }
  }

  addListenerListenForMerchant() {
    this.socket.on(this.merchantId, (event: CloverWebhook[]) => {
      event.forEach(webhook => {
        if (this.currentUser.takePayment.devicePaymentEnabled && this.currentUser.takePayment.paymentRequired && this.selectedRole === 'Kiosk' && (this.currentUser.takePayment.cloverConnectorPayment || this.currentUser.takePayment.scanToPayPayment) && this.cartService.getCartItems().length > 0) {
          if (webhook.objectId[0] === 'P') {
            this.cloverOrderService.getOrder(this.cartService.cloverOrder.id).subscribe(order => {
              if (order.paymentState === 'PAID' || order.state === 'locked') {
                this.loadRewards();
                setTimeout(() => {
                  if (this.currentUser.orgSettings.rewards.rewardsEnabled) {
                    this.processRewards(order.total);
                  } else {
                    this.firePrintAndRoute(this.cartService.cloverOrder.id);
                    this.dialog.closeAll();
                  }
                }, 500);
              }
            });
          } else if (webhook.objectId[0] === 'O') {
            // const orderId = webhook.objectId.split(':')[1];
            // if (orderId === this.cartService.cloverOrder.id) {
            //     this.cloverOrderService.getOrder(this.cartService.cloverOrder.id).subscribe(order => {
            //         if (order.paymentState === 'PAID') {
            //             this.firePrintAndRoute(orderId);
            //             this.dialog.closeAll();
            //         }
            //     });
            // }
          }
        } else if (this.selectedRole === Constants.ORDERS_DISPLAY || this.selectedRole === 'PaymentsDisplay') {
          const orderId = webhook.objectId.split(':')[1];

          this.refreshOrdersDisplay(orderId);
          // window.location.reload();
        } else if (this.selectedRole === Constants.TABLE) {
          this.cloverOrderService.getUnpaidOrder(this.currentUser.username).subscribe(order => {
            if (!order || _.isNil(order)) {
              this.dialog.closeAll();
              window.location.reload();
            }
          });
        }
      });
    });
  }

  setupForCashierRegister() {
    this.cashierRegisterActive = true;
    this.seenCashierPaymentIds.clear();
    this.ensureSocket();
    if (this.currentUser.orgSettings.accountType === Constants.CLOVER) {
      this.addCashierRegisterCloverListener();
    } else if (this.currentUser.orgSettings.accountType === Constants.SQUARE) {
      this.addCashierRegisterSquareListener();
    }
  }

  teardownCashierRegister() {
    this.cashierRegisterActive = false;
    this.seenCashierPaymentIds.clear();
  }

  // Returns true at most once per distinct Clover payment id. Called
  // synchronously (before the async getOrder) so concurrent duplicate
  // webhooks can't both pass. See seenCashierPaymentIds for rationale.
  private isNewCashierPayment(webhook: CloverWebhook): boolean {
    const paymentId = (webhook.objectId || '').split(':')[1] || webhook.objectId;
    if (!paymentId) { return false; }
    if (this.seenCashierPaymentIds.has(paymentId)) { return false; }
    this.seenCashierPaymentIds.add(paymentId);
    return true;
  }

  /**
   * Correlation guard for a single Clover payment webhook event.
   *
   * Root cause (fix/clover-payment-receipt-correlation): the original handler
   * ignored the webhook payload and printed whatever order id was in
   * localStorage['orderInFocus'], gated only on paymentState==='PAID'.
   * A previously-completed PAID order also passes that guard, so a stale id
   * from three concrete windows (checkout race, Recall browse, multi-station
   * cross-talk) sails through to firePrintRouter, producing the wrong receipt.
   *
   * Fix: fetch the order with payments expanded (getOrderWithPayments) and
   * verify the incoming payment id is actually on that order.  An old PAID
   * order fails because its payments list does not contain the new payment id.
   * This closes all three stale windows simultaneously.
   *
   * Fallback safety: if getOrderWithPayments returns a response with no
   * payments list (API expansion failure or edge-case empty order), fall back
   * to the original paymentState guard and log loudly.  A cashier unable to
   * print is worse than the intermittent wrong-receipt bug.
   */
  private _handleCloverPaymentWebhook(webhook: CloverWebhook): void {
    // objectId format: "P:<paymentId>"
    const paymentId = (webhook.objectId || '').split(':')[1];
    const orderInFocus = localStorage.getItem('orderInFocus');
    if (!orderInFocus || !paymentId) { return; }

    this.cloverOrderService.getOrderWithPayments(orderInFocus).subscribe(order => {
      // Clover returns payments as { elements: [...] }; fixOrderElementNesting
      // does NOT flatten payments, so we must check both shapes defensively.
      const payElements: any[] = order?.payments?.elements ||
                                  (Array.isArray(order?.payments) ? order.payments : []);
      const hasPaymentsData = payElements.length > 0;
      const paysThisOrder   = hasPaymentsData && payElements.some((p: any) => p?.id === paymentId);

      if (!hasPaymentsData) {
        // Payments expansion failed — fall back to original guard so the
        // cashier is not stranded.  Log so the edge case is observable.
        console.warn(
          '[cashier-clover] payments not expanded for order', orderInFocus,
          '— falling back to paymentState guard; paymentId=', paymentId
        );
      }

      const shouldFire = paysThisOrder ||
        (!hasPaymentsData && (order.paymentState === 'PAID' || order.state === 'locked'));

      if (shouldFire) {
        this.dialog.closeAll();
        this.paymentCompleted$.next({orderId: orderInFocus});
      }
    });
  }

  private addCashierRegisterCloverListener() {
    this.socket.on(this.merchantId + '_cashier', (event: CloverWebhook[]) => {
      if (!this.cashierRegisterActive) { return; }
      event.forEach(webhook => {
        if (webhook.objectId[0] === 'P') {
          if (!this.isNewCashierPayment(webhook)) { return; }
          this._handleCloverPaymentWebhook(webhook);
        }
      });
    });
    // Also listen on the main merchant channel
    if (!this.socket.listeners(this.merchantId).some(l => l._cashierHandler)) {
      const handler: any = (event: CloverWebhook[]) => {
        if (!this.cashierRegisterActive) { return; }
        event.forEach(webhook => {
          if (webhook.objectId[0] === 'P') {
            if (!this.isNewCashierPayment(webhook)) { return; }
            this._handleCloverPaymentWebhook(webhook);
          }
        });
      };
      handler._cashierHandler = true;
      this.socket.on(this.merchantId, handler);
    }
  }

  private addCashierRegisterSquareListener() {
    if (!this.socket.listeners(this.merchantId).some(l => l._cashierHandler)) {
      const handler: any = (event: any) => {
        if (!this.cashierRegisterActive) { return; }
        [event].forEach(webhook => {
          const orderInFocus = localStorage.getItem('orderInFocus');
          if (!orderInFocus) { return; }
          if (webhook.type === 'terminal.checkout.updated' &&
              webhook.data?.object?.checkout?.order_id === orderInFocus &&
              webhook.data?.object?.device_code?.status === 'COMPLETED') {
            this.dialog.closeAll();
            this.paymentCompleted$.next({orderId: orderInFocus});
          } else if (webhook.type === 'payment.updated' &&
                     webhook.data?.object?.payment?.order_id === orderInFocus &&
                     webhook.data?.object?.payment?.status === 'COMPLETED') {
            this.dialog.closeAll();
            this.paymentCompleted$.next({orderId: orderInFocus});
          }
        });
      };
      handler._cashierHandler = true;
      this.socket.on(this.merchantId, handler);
    }
  }

  refreshOrdersDisplay(orderId) {
    this.newOrderId.next(orderId);
    this.newOrderCreated.next(true);
  }

  addListenerListenForOrg() {
    this.ensureSocket();
    this.socket.on(this.currentUser.orgSettings.orgUuid, (webhook: GeneralWebhook[]) => {
      if (webhook) {
        const parsedWebhook = webhook[this.currentUser.orgSettings.orgUuid];
        this.processOrgWebhook(parsedWebhook);
      }
    });
  }

  firePrintAndRoute(orderId) {
    this.cloverOrderService.fireOrder(orderId, this.currentUser.orgSettings.featuresEnabled.v1Print).subscribe(response => {
      this.rewardsService.customer = new Observable<Customer>();
      this.clearDataAndRouteToNextComnponent();
    });
  }

  private clearDataAndRouteToNextComnponent() {
    let oldUser;
    localStorage.setItem('CartItem', this._AESEncryptDecryptService.encrypt(JSON.stringify([])));

    if (this._AESEncryptDecryptService.decrypt(localStorage.getItem('oldUser'))) {
      oldUser = JSON.parse(this._AESEncryptDecryptService.decrypt(localStorage.getItem('oldUser')));
    }

    if (oldUser) {
      this.utilsService.setCurrentUser(oldUser);
      localStorage.removeItem('oldUser');
    }
    this.rewardsService.customer = new Observable<Customer>();
    this.cartService.removeAllFromCart();
    this.cartService.removeOrderHistory();
    this.cartService.tip = 0;
    // console.log('I\'m continuing');
    this.alertService.success('Order Created and Payment Received', true);
    //  this.cartService.removeAllFromCart();
    this.routeToNextPage(this.currentUser);
  }

  private routeToNextPage(oldUser?) {
    this.router.navigate(['order/orderConfirmation', {
      orderId: this.cartService.cloverOrder.id
    }]);
  }

  processRewards(totalAmount) {
    if (this.selectedRole === 'Kiosk' && this.rewardsCustomer?.number > 0) {
      if (this.currentUser.orgSettings.rewards.rewardsEnabled && (this.rewardsCustomer.points < this.currentUser.orgSettings.rewards.afterVisits) && !this.rewardsUpdated) {
        this.rewardsUpdated = true;
        this.rewardsService.checkin(Number(this.rewardsCustomer.number),
          this.rewardsCustomer.name).subscribe((response) => {
            if (response) {
              this.firePrintAndRoute(this.cartService.cloverOrder.id);
              this.rewardsUpdated = false;
              this.dialog.closeAll();
            }
          },
          error => {
            console.error('error: The Order has been created but failed to apply award');
            this.rewardsUpdated = false;
            this.firePrintAndRoute(this.cartService.cloverOrder.id);
            this.dialog.closeAll();
          });
      } else if (this.currentUser.orgSettings.rewards.rewardsEnabled && (this.rewardsCustomer.points >= this.currentUser.orgSettings.rewards.afterVisits) && !this.rewardsRedeemed) {
        this.rewardsRedeemed = true;
        const discount = new Discount();
        discount.name = '$' + Number(this.currentUser.orgSettings.rewards.amountOff) / 100 + ' off after '
          + Number(this.currentUser.orgSettings.rewards.afterVisits) + ' visits';
        discount.amount = Number(this.currentUser.orgSettings.rewards.amountOff);
        const total = totalAmount + discount.amount;
        this.rewardsService.redeemPaidOrder(Number(this.rewardsCustomer.number), discount, this.currentUser.orgSettings.rewards.afterVisits).subscribe(response => {
            if (response) {
              this.rewardsRedeemed = false;
              this.firePrintAndRoute(this.cartService.cloverOrder.id);
              this.dialog.closeAll();
            }
          },
          error => {
            this.rewardsRedeemed = false;
            console.error('error: The Order has been created but failed to apply award');
            this.firePrintAndRoute(this.cartService.cloverOrder.id);
            this.dialog.closeAll();
          });
      }
    } else {
      this.firePrintAndRoute(this.cartService.cloverOrder.id);
      this.dialog.closeAll();
    }
    this.rewardsService.customer = new Observable<Customer>();
  }

  private loadRewards() {
    this.rewardsService.customer?.subscribe(customer => {
      // console.log('customer ' + JSON.stringify(customer));
      this.rewardsCustomer = customer;
    });
  }

  private processOrgWebhook(parsedWebhook: any) {
    if (this.selectedRole === Constants.ORDERS_DISPLAY) {
      if (parsedWebhook.type === 'BumpedOrder' && parsedWebhook.status === 'Created') {
        this.bumpedOrders.push(parsedWebhook.object);
        this.bumpedOrdersSubject$.next(this.bumpedOrders);
      } else if (parsedWebhook.type === 'BumpedOrder' && parsedWebhook.status === 'Deleted') {
        this.bumpedOrders = _.filter(this.bumpedOrders, bumpedOrder => bumpedOrder.orderId !== parsedWebhook.orderId);
        this.bumpedOrdersSubject$.next(this.bumpedOrders);
      } else if (parsedWebhook.type === 'BumpedOrder' && parsedWebhook.status === 'Updated') {
        const index = _.findIndex(this.bumpedOrders, bumpedOrder => bumpedOrder.orderId === parsedWebhook.orderId);
        this.bumpedOrders[index] = parsedWebhook.object;
        this.bumpedOrdersSubject$.next(this.bumpedOrders);
      }
    }
    // all users webhook
    if (parsedWebhook.type === 'OrgSettings' && parsedWebhook.status === 'Updated') {
      this.currentUser.orgSettings = parsedWebhook.object;
      this.utilsService.setCurrentUser(this.currentUser);
    }

  }


  // square webhook
  addListenerListenForSquareMerchant() {
    this.socket.on(this.merchantId, (event: any) => {
      [event].forEach(webhook => {
        if (this.selectedRole === 'Kiosk' && this.currentUser.takePayment.scanToPayPayment) {
          if (webhook.type === 'payment.updated' && webhook.data.object.payment.order_id === this.cartService.orderId && webhook.data.object.payment.status === 'COMPLETED') {
            console.log('order: ' + this.cartService.orderId + ' has been paid');
            this.dialog.closeAll();
            this.clearDataAndRouteToNextComnponentSquare();
          }
        } else if (this.selectedRole === 'Kiosk' && this.currentUser.takePayment.cloverConnectorPayment) {
          if (webhook.type === 'terminal.checkout.updated' && webhook.data.object.checkout.order_id === this.cartService.orderId && webhook.data.object.device_code.status === 'COMPLETED') {
            console.log('order: ' + this.cartService.orderId + ' has been paid');
            this.dialog.closeAll();
            this.clearDataAndRouteToNextComnponentSquare();
          }
        } else if (this.selectedRole === 'Table' && this.currentUser.takePayment.scanToPayPayment) {
          if (webhook.type === 'payment.updated' && webhook.data.object.payment.order_id === this.cartService.orderId && webhook.data.object.payment.status === 'COMPLETED') {
            console.log('order: ' + this.cartService.orderId + ' has been paid');
            this.dialog.closeAll();
            this.clearDataAndRouteToNextComnponentSquare();
          }
        } else if (this.selectedRole === 'Table' && this.currentUser.takePayment.cloverConnectorPayment) {
          if (webhook.type === 'terminal.checkout.updated' && webhook.data.object.checkout.order_id === this.cartService.orderId && webhook.data.object.device_code.status === 'COMPLETED') {
            console.log('order id: ' + this.cartService.orderId + ' has been paid');
            this.dialog.closeAll();
            this.clearDataAndRouteToNextComnponentSquare();
          }
        } else if (this.selectedRole === 'Admin' || this.selectedRole === 'Manager') {
          if (webhook.type === 'device.code.paired' && webhook.data.object.device_code.status === 'PAIRED') {
            this.squareDeviceId = webhook.data.object.device_code.device_id;
            this.deviceAlias = webhook.data.object.device_code.name;
            this.dialog.closeAll();
          }
          if ((webhook.type === 'terminal.checkout.updated' || webhook.type === 'payment.updated') && webhook.data.object.checkout.order_id === localStorage.getItem('orderInFocus') && webhook.data.object.device_code.status === 'COMPLETED') {
            this.squareDeviceId = webhook.data.object.device_code.device_id;
            this.deviceAlias = webhook.data.object.device_code.name;
            this.dialog.closeAll();
          }
        } else if (this.selectedRole === 'OrdersDisplay') {
          if (webhook.type === 'order.updated') {
            const index = this.orders.findIndex(order => order.id === webhook.data.object.order_id);
            if (index !== -1) {
              this.squareApiService.getOrderById(webhook.data.object.order_id).subscribe(order => {
                this.orders[index] = order;
                this.OrdersSubject$.next(this.orders);
              });
            }
          }
        }
      });
    });
  }

  addListenerListenForSquareDevWebhookTestMerchant() {
    this.socket.on('MLQDCYHT32HDD', (event: any) => {
      [event].forEach(webhook => {
        if (this.selectedRole === 'Kiosk' && this.currentUser.takePayment.scanToPayPayment) {
          if (webhook.type === 'payment.updated' && webhook.data.object.payment.order_id === this.cartService.orderId && webhook.data.object.payment.status === 'COMPLETED') {
            console.log('order: ' + this.cartService.orderId + ' has been paid');
            this.dialog.closeAll();
            this.clearDataAndRouteToNextComnponentSquare();
          }
        } else if (this.selectedRole === 'Kiosk' && this.currentUser.takePayment.cloverConnectorPayment) {
          if (webhook.type === 'terminal.checkout.updated' && webhook.data.object.checkout.order_id === this.cartService.orderId && webhook.data.object.device_code.status === 'COMPLETED') {
            console.log('order: ' + this.cartService.orderId + ' has been paid');
            this.dialog.closeAll();
            this.clearDataAndRouteToNextComnponentSquare();
          }
        } else if (this.selectedRole === 'Table' && this.currentUser.takePayment.scanToPayPayment) {
          if (webhook.type === 'payment.updated' && webhook.data.object.payment.order_id === this.cartService.orderId && webhook.data.object.payment.status === 'COMPLETED') {
            console.log('order: ' + this.cartService.oldTableOrder.id + ' has been paid');
            this.dialog.closeAll();
            this.clearDataAndRouteToNextComnponentSquare();
          }
        } else if (this.selectedRole === 'Table' && this.currentUser.takePayment.cloverConnectorPayment) {
          if (webhook.type === 'terminal.checkout.updated' && webhook.data.object.checkout.order_id === this.cartService.orderId && webhook.data.object.device_code.status === 'COMPLETED') {
            console.log('order id: ' + this.cartService.oldTableOrder.id + ' has been paid');
            this.cartService.orderId = webhook.data.object.checkout.order_id;
            this.dialog.closeAll();
            this.clearDataAndRouteToNextComnponentSquare();
          }
        } else if (this.selectedRole === 'Table' && !this.currentUser.takePayment.cloverConnectorPayment && !this.currentUser.takePayment.scanToPayPayment) {
          if (webhook.type === 'terminal.checkout.updated' && webhook.data.object.checkout.order_id === this.cartService.orderId && webhook.data.object.device_code.status === 'COMPLETED') {
            console.log('order id: ' + this.cartService.oldTableOrder.id + ' has been paid');
            this.deleteSquareOrder();
          }
        } else if (this.selectedRole === 'Admin' || this.selectedRole === 'Manager') {
          if (webhook.type === 'device.code.paired' && webhook.data.object.device_code.status === 'PAIRED') {
            this.squareDeviceId = webhook.data.object.device_code.device_id;
            this.deviceAlias = webhook.data.object.device_code.name;
            this.dialog.closeAll();
          }
          if ((webhook.type === 'terminal.checkout.updated' || webhook.type === 'payment.updated') && webhook.data.object.checkout.order_id === localStorage.getItem('orderInFocus') && webhook.data.object.device_code.status === 'COMPLETED') {
            this.deleteSquareOrder();
          }
        }
      });
    });
  }

  private clearDataAndRouteToNextComnponentSquare() {
    let oldUser;
    localStorage.setItem('CartItem', JSON.stringify([]));

    if (localStorage.getItem('oldUser')) {
      oldUser = JSON.parse(localStorage.getItem('oldUser'));
    }

    if (oldUser) {
      localStorage.setItem('currentUser', this._AESEncryptDecryptService.encrypt(JSON.stringify(oldUser)));
      localStorage.removeItem('oldUser');
    }
    this.cartService.removeAllFromCart();
    if (this.selectedRole !== 'Table') {
      this.cartService.orderId = '';
    }
    this.cartService.oldTableOrder = undefined;
    // console.log('I\'m continuing');
    this.alertService.success('Order Created and Payment Received', true);
    //  this.cartService.removeAllFromCart();
    if (this.selectedRole === 'Kiosk' || this.selectedRole === Constants.QR_ORDER) {
      this.router.navigate(['order/orderConfirmation', {
        orderId: this.cartService.orderId
      }]);
    } else if (this.selectedRole === 'Admin' || this.selectedRole === 'Manager') {
      this.router.navigate(['/sidebar/serverOrder']);
    }
    this.cartService.orderId = '';
  }

  deleteSquareOrder() {
    this.squareApiService.deleteOpenOrder().subscribe(response => {
      this.dialog.closeAll();
      this.clearDataAndRouteToNextComnponentSquare();
    });
  }
}
